import React, { useMemo, useState } from 'react';

import arrayMutators from 'final-form-arrays';

import { Divider } from '@vk-hr-tek/ui/Divider';
import { Form } from '@vk-hr-tek/app-ui/form';
import { Box } from '@vk-hr-tek/ui/Box';
import { Button } from '@vk-hr-tek/ui/Button';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { useInject } from '@vk-hr-tek/core/ioc';

import { useDispatch } from '@app/hooks';
import { ContentSurveyItem } from '@app/gen/community';

import { ContentSurveysPassingDto } from '../../../dto';
import { ContentSurveysPassingSubmitData } from '../../../types';
import { ContentSurveysPassingMapper } from '../../../mappers';
import { ContentSurveysItemQuestion } from '../ContentSurveysItemQuestion';
import { startDownloadSurveyReport } from '../../../slice';
import { ContentSurveysItemContainer } from '../ContentSurveysItemContainer';
import { ContentSurveysItemCompleted } from '../ContentSurveysItemCompleted';
import { ContentSurveysItemAnalytics } from '../ContentSurveysItemAnalytics';

interface ContentSurveysItemPassingProps {
  survey: ContentSurveyItem;
  onSubmit?: (data: ContentSurveysPassingSubmitData) => Promise<unknown>;
  preview?: boolean;
}

export const ContentSurveysItemPassing = ({
  survey,
  onSubmit,
  preview,
}: ContentSurveysItemPassingProps) => {
  const dispatch = useDispatch();
  const mapper = useInject(ContentSurveysPassingMapper);

  const [isSurveyAnalyticsOpen, setSurveyAnalyticsOpen] = useState(false);

  const processedSurvey = useMemo(() => {
    return mapper.processSurveyPassing(survey);
  }, [mapper, survey]);

  const initialValues = useMemo<ContentSurveysPassingDto>(() => {
    return mapper.processSurveyQuestions(processedSurvey.questions);
  }, [mapper, processedSurvey]);

  const openSurveyAnalytics = () => setSurveyAnalyticsOpen(true);
  const closeSurveyAnalytics = () => setSurveyAnalyticsOpen(false);

  const handleFormSubmit = async (formValues: ContentSurveysPassingDto) => {
    if (processedSurvey.id) {
      await onSubmit?.({
        surveyId: processedSurvey.id,
        version: processedSurvey.version || 0,
        answers: mapper.processSurveyAnswers(formValues),
      });
    }
  };

  const handleDownloadReport = () => {
    if (processedSurvey.id) {
      dispatch(
        startDownloadSurveyReport({ content_survey_id: processedSurvey.id }),
      );
    }
  };

  if (isSurveyAnalyticsOpen) {
    return (
      <ContentSurveysItemAnalytics
        survey={processedSurvey}
        closeSurveyAnalytics={closeSurveyAnalytics}
        onDownloadReport={handleDownloadReport}
      />
    );
  }

  if (processedSurvey.isCompleted || processedSurvey.isPassing) {
    return (
      <ContentSurveysItemContainer survey={processedSurvey}>
        <ContentSurveysItemCompleted
          survey={processedSurvey}
          openSurveyAnalytics={openSurveyAnalytics}
          onDownloadReport={handleDownloadReport}
        />
      </ContentSurveysItemContainer>
    );
  }

  return (
    <Form<ContentSurveysPassingDto>
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      onSubmit={handleFormSubmit}
      render={({ submitting, handleSubmit, hasValidationErrors }) => (
        <ContentSurveysItemContainer survey={processedSurvey}>
          <Box display="flex" flexDirection="column" gap="8" px="24" py="16">
            {processedSurvey.questions.map((question, index) => (
              <ContentSurveysItemQuestion
                key={question.id}
                fieldIndex={index}
                question={question}
              />
            ))}
          </Box>

          <Divider />

          <Box px="24" py="20">
            <Box display="flex" justifyContent="space-between">
              <Tooltip
                placement="top-start"
                title="Опрос нельзя завершить в режиме предварительного просмотра"
                disabled={!preview}
              >
                <Box>
                  <Button
                    size="small"
                    variant="primary"
                    loading={submitting}
                    disabled={hasValidationErrors || submitting || preview}
                    onClick={handleSubmit}
                  >
                    Отправить ответ
                  </Button>
                </Box>
              </Tooltip>

              <Box display="flex" gap="16">
                {processedSurvey.actions?.results && (
                  <Tooltip
                    placement="top"
                    title="Результаты нельзя просмотреть в режиме предварительного просмотра"
                    disabled={!preview}
                  >
                    <Box>
                      <Button
                        size="small"
                        variant="quaternary"
                        onClick={openSurveyAnalytics}
                        startIcon={
                          <Icon
                            name="poll"
                            size="16"
                            color={
                              !preview
                                ? 'text.light.primary'
                                : 'text.light.tertirary'
                            }
                          />
                        }
                        disabled={preview}
                      >
                        Результаты
                      </Button>
                    </Box>
                  </Tooltip>
                )}

                {processedSurvey.actions?.report && (
                  <Tooltip
                    placement={preview ? 'top-end' : 'top'}
                    title={
                      preview
                        ? 'Отчет нельзя скачать в режиме предварительного просмотра'
                        : 'Скачать отчет (.xlsx)'
                    }
                  >
                    <Box>
                      <Button
                        size="small"
                        onClick={handleDownloadReport}
                        variant="tertiaryLight"
                        startIcon={
                          <Icon
                            name="download_outline"
                            size="16"
                            color="text.light.primary"
                          />
                        }
                        disabled={preview}
                      >
                        Отчет
                      </Button>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        </ContentSurveysItemContainer>
      )}
    />
  );
};
